import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  MenuItem,
  Select,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Autocomplete,
  CircularProgress,
  Tooltip,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search"; // Added for search icon
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import Restrict from "../../Restrict";
import DynamicTable from "../../../components/lab-dynamic-table";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "../../../components/Modals/delete-modal";
import useDebounce from "../../../hooks/useDebounce";
import { formatDateTime, formatDateOnly } from "../../../utils/dateTimeUtils";
// import DynamicTable from '../../components/Dynamic-table';
import { formatCurrency } from "../../../utils/numberUtils";

interface IncomeCategory {
  _id: string;
  categoryName: string;
  projectId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  isActive: boolean;
}

interface SourceType {
  _id: string;
  sourseTypeName: string;
  projectId?: string;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
  isDeleted?: boolean;
  isActive?: boolean;
}
interface Patient {
  _id: string;
  patientName: string;
  guardiansName: string;
  gender: string;
  dob: string;
  phonNumber: string;
  cnic: string;
  healthId: string;
  city: string;
  reference: string;
  projectId: string;
  userId: string;
  mrn: number;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  isActive: boolean;
  isOnlinePt?: boolean;
}

interface Income {
  _id: string;
  incomeCategory: string;
  date: string;
  amount: number;
  incomeSourseType: string;
  description: string;
  payment_mode: string;
  patientId: string;
  projectId: string;
  userId: string;
  receiptNo: number;
  createdAt: string;
  createdBy: any; // Adjust type as per your user structure
  updatedAt: string;
  isDeleted: boolean;
  isActive: boolean;
  attachmentUrl?: string; // Add this line
  attachmentPath?: string; // Add this line

  attachment?: string;
  patientInfo?: {
    // Add this new field
    patientName: string;
    mrn: number;
    guardiansName: string;
    phonNumber: string;
  };
}

const DEFAULT_SOURCE_TYPE_OPTIONS = [
  "Consultation",
  "Surgery",
  "Laboratory",
  "Radiology",
  "Pharmacy",
  "Bed",
  "Emergency",
  "Procedure",
  "Bloodbank",
  "Nursing",
  "Ambulance",
  "Registration",
  "Miscellaneous",
  "Other",
];
const IncomeCategory: React.FC = () => {
  const [categoryName, setCategoryName] = useState<string>("");
  const [categories, setCategories] = useState<IncomeCategory[]>([]);
  const [editCategoryId, setEditCategoryId] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState<string>("");
  const [openMainDialog, setOpenMainDialog] = useState<boolean>(false);
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [patientOptions, setPatientOptions] = useState<Patient[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<IncomeCategory | null>(null);
  const [fieldErrors, setFieldErrors] = useState({
    incomeCategory: false,
    description: false,
    amount: false,
    incomeSourseType: false,
    payment_mode: false,
  });
  const [incomeForm, setIncomeForm] = useState<{
    incomeCategory: string;
    date: string;
    amount: number;
    incomeSourseType: string;
    description: string;
    payment_mode: string;
    attachment: File | string | null;
    patientId: string;
  }>({
    incomeCategory: "",
    date: new Date().toISOString().slice(0, 10),
    amount: 0,
    incomeSourseType: "",
    description: "",
    payment_mode: "Cash",
    patientId: "",
    attachment: null,
  });
  const navigate = useNavigate();
  const [editIncome, setEditIncome] = useState<Income | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [sourceTypes, setSourceTypes] = useState<SourceType[]>([]);
  const [startDate, setStartDate] = useState<string>(
    new Date().toISOString().slice(0, 10)
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().slice(0, 10)
  );
  // Add date range dropdown state
  const [dateRange, setDateRange] = useState("Today");
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    type: "income" | "category";
  } | null>(null);
  // Add these new state variables after existing ones
  const [openImageModal, setOpenImageModal] = useState<boolean>(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  // New states for patient search dialog
  const [openPatientSearchDialog, setOpenPatientSearchDialog] =
    useState<boolean>(false);
  const [searchBy, setSearchBy] = useState<string>("name");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  // Delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [deleteReason, setDeleteReason] = useState<string>("");
  const [deleteItemId, setDeleteItemId] = useState<string>("");

  // Calculate total income amount
  const totalIncomeAmount = incomes.reduce(
    (sum, income) => sum + parseFloat(income.amount.toString()),
    0
  );
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const projectProfile = useSelector(
    (state: any) => state.projectProfileSlice.projectProfile
  );
  const hospitalName = projectProfile?.hospitalName || "N/A";
  const incomePermissions = profile?.data?.accessConfigurations?.Income || {};

  const initialForm = {
    incomeCategory: "",
    date: new Date().toISOString().slice(0, 10),
    amount: 0,
    incomeSourseType: "",
    description: "",
    payment_mode: "Cash",
    patientId: "",
    attachment: null,
  };
  const [editClick, setEditClick] = useState(false);
  useEffect(() => {
    if (incomePermissions.view_module) {
      fetchCategories();
      fetchIncomes();
      fetchPatients(); // This should now work correctly
      fetchSourceTypes();
    }

    // cleanup part (runs on unmount or dependency change)
    return () => {
      if (incomeForm.attachment instanceof File && selectedImageUrl) {
        URL.revokeObjectURL(selectedImageUrl);
      }
    };
  }, [
    incomePermissions.view_module,
    page,
    rowsPerPage,
    incomeForm.attachment,
    selectedImageUrl,
  ]);

  // Auto-fetch data when dates change
  useEffect(() => {
    if (incomePermissions.view_module) {
      fetchIncomes();
    }
  }, [startDate, endDate, debouncedSearchTerm]);

  // Fetch source types from API
  const fetchSourceTypes = async (): Promise<void> => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/sourse-type/getAllBySearch`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.isSuccess && Array.isArray(response.data.data)) {
        // Store the full objects for the form dropdown
        console.log("Source types loaded from API:", response.data.data);
        setSourceTypes(response.data.data);
      } else {
        // fallback - convert strings to objects with sourseTypeName property
        const fallbackOptions: SourceType[] = DEFAULT_SOURCE_TYPE_OPTIONS.map(
          (option, index) => ({
            _id: `fallback-${index}`,
            sourseTypeName: option,
          })
        );
        console.log("Using fallback source types:", fallbackOptions);
        setSourceTypes(fallbackOptions);
      }
    } catch (err: any) {
      console.warn("Failed to fetch source types:", err);
      // fallback - convert strings to objects with sourseTypeName property
      const fallbackOptions: SourceType[] = DEFAULT_SOURCE_TYPE_OPTIONS.map(
        (option, index) => ({
          _id: `fallback-${index}`,
          sourseTypeName: option,
        })
      );
      console.log(
        "Error occurred, using fallback source types:",
        fallbackOptions
      );
      setSourceTypes(fallbackOptions);
    }
  };

  if (!incomePermissions.view_module) {
    return <Restrict />;
  }

  const token = localStorage.getItem("token");

  const fetchCategories = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income-category/getAllBySearch`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.isSuccess) {
        setCategories(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch categories");
      }
    } catch (err: any) {
      console.warn("Income categories endpoint not available:", err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomes = async (): Promise<void> => {
    if (!incomePermissions.view_report) {
      toast.error("You do not have permission to view incomes.");
      return;
    }

    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_ADMIN_API_URL}/income/getAll`;
      const params = new URLSearchParams();

      params.append("count", rowsPerPage.toString());
      params.append("pageNo", (page + 1).toString());

      if (startDate) {
        params.append("fromDate", startDate);
      }
      if (endDate) {
        params.append("toDate", endDate);
      }
      if (!startDate && !endDate) {
        const today = new Date().toISOString().slice(0, 10);
        params.append("fromDate", today);
        params.append("toDate", today);
      }
      if (searchBy && searchTerm) {
        const backendSearchBy =
          searchBy === "name"
            ? "Pt Name"
            : searchBy === "mrn"
            ? "MRN"
            : searchBy === "Category"
            ? "Category"
            : "Sourse Type";
        params.append("searchBy", backendSearchBy);
        params.append("search", searchTerm);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.isSuccess) {
        const filteredIncomes = response.data.data || [];
        setIncomes(filteredIncomes);
        setTotalCount(response.data.totalCount || filteredIncomes.length);
      } else {
        toast.error(response.data.message || "Failed to fetch incomes");
        setIncomes([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error("Fetch Incomes Error:", err.response || err);
      toast.error(
        err.response?.data?.message ||
          "An error occurred while fetching incomes"
      );
      setIncomes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = async (newCategory: IncomeCategory | null) => {
    setSelectedCategory(newCategory);
    setPage(0);

    if (newCategory) {
      setLoading(true);
      try {
        const params = new URLSearchParams();

        params.append("count", rowsPerPage.toString());
        params.append("pageNo", "1");

        params.append("incomeCategory", newCategory.categoryName);
        if (startDate) {
          params.append("fromDate", startDate);
        }
        if (endDate) {
          params.append("toDate", endDate);
        }

        const url = `${
          process.env.REACT_APP_ADMIN_API_URL
        }/income/getAll?${params.toString()}`;

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.isSuccess) {
          const filteredIncomes = response.data.data || [];
          setIncomes(filteredIncomes);
          setTotalCount(response.data.totalCount || filteredIncomes.length);
        } else {
          toast.error(
            response.data.message || "Failed to fetch incomes by category"
          );
          setIncomes([]);
          setTotalCount(0);
        }
      } catch (err: any) {
        console.error("Category Filter Error:", err.response || err);
        toast.error(
          err.response?.data?.message ||
            "An error occurred while filtering incomes by category"
        );
        setIncomes([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    } else {
      fetchIncomes();
    }
  };

  // Fixed fetchPatients function
  const fetchPatients = async () => {
    setLoading(true);
    try {
      // Use the correct endpoint name as shown in your API documentation
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/patient-registration/getMostResetPatients`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
        }
      );

      if (response.data.isSuccess) {
        // Map the actual API response structure to your Patient interface
        const patients = response.data.data.map((patient: any) => ({
          _id: patient._id,
          patientName: patient.patientName,
          guardiansName: patient.guardiansName || "",
          gender: patient.gender || "",
          dob: patient.dob || "", // Not in API response, set as empty
          phonNumber: patient.phonNumber || "",
          cnic: patient.cnic || "", // Not in API response, set as empty
          healthId: patient.healthId || "", // Not in API response, set as empty
          city: patient.city || "", // Not in API response, set as empty
          reference: patient.reference || "", // Not in API response, set as empty
          projectId: patient.projectId || "", // Not in API response, set as empty
          userId: patient.userId || "", // Not in API response, set as empty
          mrn: patient.mrn || 0,
          createdAt: patient.createdAt || "", // Not in API response, set as empty
          createdBy: patient.createdBy || {}, // Not in API response, set as empty object
          updatedAt: patient.updatedAt || "", // Not in API response, set as empty
          isDeleted: false, // Not in API response, default to false
          isActive: true, // Not in API response, default to true
          isOnlinePt: false, // Not in API response, default to false
        }));

        console.log("Fetched patients:", patients); // Debug log
        setPatientOptions(patients);
      } else {
        toast.error(response.data.message || "Failed to fetch patients");
        setPatientOptions([]);
      }
    } catch (err: any) {
      console.error("Fetch Patients Error:", err.response || err);
      toast.error(
        err.response?.data?.message ||
          "An error occurred while fetching patients"
      );
      setPatientOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fixed handlePatientSearch function
  const handlePatientSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error("Please enter a search term");
      return;
    }

    setSearchLoading(true);
    try {
      // Use environment variable instead of hardcoded URL
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/patient-registration/searchPatient`,
        {
          params: {
            searchBy: searchBy,
            search: searchTerm.trim(),
          },
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
        }
      );

      if (response.data.isSuccess) {
        setSearchResults(response.data.data || []);
        if (response.data.data?.length === 0) {
          toast.info("No patients found matching your search criteria");
        }
      } else {
        toast.error(response.data.message || "Failed to search patients");
        setSearchResults([]);
      }
    } catch (err: any) {
      console.error("Patient Search Error:", err.response || err);
      toast.error(
        err.response?.data?.message ||
          "An error occurred while searching patients"
      );
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Fixed handleSelectPatient function
  const handleSelectPatient = (patient: Patient) => {
    // Update the form with selected patient
    setIncomeForm({ ...incomeForm, patientId: patient._id });

    // Add to patientOptions if not already there (for dropdown)
    const existingPatient = patientOptions.find(p => p._id === patient._id);
    if (!existingPatient) {
      // Add the selected patient to the beginning of the options array
      setPatientOptions([patient, ...patientOptions.slice(0, 99)]);
    }

    // Close the search dialog
    setOpenPatientSearchDialog(false);

    // Clear search results and term
    setSearchResults([]);
    setSearchTerm("");

    toast.success(`Patient "${patient.patientName}" selected successfully`);
  };

  // Date range handler similar to Summary page
  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    const today = new Date();
    const formatDate = (date: Date) => date.toISOString().split("T")[0];

    switch (value) {
      case "Today":
        setStartDate(formatDate(today));
        setEndDate(formatDate(today));
        break;
      case "Yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        setStartDate(formatDate(yesterday));
        setEndDate(formatDate(yesterday));
        break;
      case "Last 7 days":
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        setStartDate(formatDate(sevenDaysAgo));
        setEndDate(formatDate(today));
        break;
      case "Last 1 month":
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 1);
        setStartDate(formatDate(oneMonthAgo));
        setEndDate(formatDate(today));
        break;
      case "Last 6 months":
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        setStartDate(formatDate(sixMonthsAgo));
        setEndDate(formatDate(today));
        break;
      case "Last 1 year":
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        setStartDate(formatDate(oneYearAgo));
        setEndDate(formatDate(today));
        break;
      case "custom":
        break;
      default:
        break;
    }
  };

  // Alternative fetchPatients with fallback
  const fetchPatientsWithFallback = async () => {
    setLoading(true);
    try {
      // Try the primary endpoint first
      let response;
      try {
        response = await axios.get(
          `${process.env.REACT_APP_ADMIN_API_URL}/patient-registration/getMostRecentPatients`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "*/*",
            },
          }
        );
      } catch (primaryError: any) {
        // If primary endpoint fails, try alternative endpoint
        console.warn("Primary endpoint failed, trying alternative...");
        response = await axios.get(
          `${process.env.REACT_APP_ADMIN_API_URL}/patient-registration/getAllBySearch`,
          {
            params: {
              count: 100,
              pageNo: 1,
              sortBy: "createdAt",
              sortOrder: "desc",
            },
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "*/*",
            },
          }
        );
      }

      if (response.data.isSuccess) {
        const patients = (response.data.data || []).map((patient: any) => ({
          _id: patient._id,
          patientName: patient.patientName,
          guardiansName: patient.guardiansName || "",
          gender: patient.gender || "",
          dob: patient.dob || "",
          phonNumber: patient.phonNumber || "",
          cnic: patient.cnic || "",
          healthId: patient.healthId || "",
          city: patient.city || "",
          reference: patient.reference || "",
          projectId: patient.projectId || "",
          userId: patient.userId || "",
          mrn: patient.mrn || 0,
          createdAt: patient.createdAt || "",
          updatedAt: patient.updatedAt || "",
          isDeleted: patient.isDeleted || false,
          isActive: patient.isActive !== undefined ? patient.isActive : true,
          isOnlinePt: patient.isOnlinePt || false,
        }));

        setPatientOptions(patients.slice(0, 100));
      } else {
        toast.error(response.data.message || "Failed to fetch patients");
        setPatientOptions([]);
      }
    } catch (err: any) {
      console.error("Fetch Patients Error:", err.response || err);
      toast.error(
        "Unable to load recent patients. You can still search for patients using the search feature."
      );
      setPatientOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: IncomeCategory): void => {
    if (!incomePermissions.add_income) {
      toast.error("You do not have permission to edit categories.");
      return;
    }
    setEditCategoryId(category._id);
    setEditCategoryName(category.categoryName);
    setCategoryName(category.categoryName);
  };

  const handleUpdate = async (categoryId: string): Promise<void> => {
    if (!incomePermissions.add_income) {
      toast.error("You do not have permission to update categories.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_ADMIN_API_URL}/income-category/updateById/${categoryId}`,
        { categoryName: editCategoryName || categoryName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.isSuccess) {
        toast.success("Category updated successfully");
        setEditCategoryId(null);
        setEditCategoryName("");
        setCategoryName("");
        fetchCategories();
      } else {
        toast.error(response.data.message || "Failed to update category");
      }
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          "An error occurred while updating the category"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = (): void => {
    setEditCategoryId(null);
    setEditCategoryName("");
    setCategoryName("");
  };

  const handleOpenDeleteDialog = (
    id: string,
    type: "income" | "category"
  ): void => {
    if (!incomePermissions.add_income) {
      toast.error(`You do not have permission to delete ${type}s.`);
      return;
    }
    setDeleteItemId(id);
    setItemToDelete({ id, type });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = (): void => {
    setIsDeleteModalOpen(false);
    setDeleteReason("");
    setDeleteItemId("");
    setItemToDelete(null);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!itemToDelete || !deleteReason.trim()) {
      toast.error("Please provide a reason for deletion");
      return;
    }

    setLoading(true);
    try {
      const url =
        itemToDelete.type === "category"
          ? `${process.env.REACT_APP_ADMIN_API_URL}/income-category/deleteById/${itemToDelete.id}`
          : `${process.env.REACT_APP_ADMIN_API_URL}/income/deleteById/${itemToDelete.id}`;

      const response = await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` },
        data: { deleteReason: deleteReason },
      });

      if (response.data.isSuccess) {
        toast.success(
          `${
            itemToDelete.type.charAt(0).toUpperCase() +
            itemToDelete.type.slice(1)
          } deleted successfully`
        );
        if (itemToDelete.type === "category") {
          fetchCategories();
        } else {
          fetchIncomes();
        }
        handleDeleteModalClose();
      } else {
        toast.error(
          response.data.message || `Failed to delete ${itemToDelete.type}`
        );
      }
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          `An error occurred while deleting the ${itemToDelete.type}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    fetchCategories();
    if (!incomePermissions.add_income) {
      toast.error("You do not have permission to add categories.");
      return;
    }
    if (editCategoryId) {
      await handleUpdate(editCategoryId);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_URL}/income-category`,
        { categoryName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.isSuccess) {
        toast.success(
          response.data.message || "Income category added successfully"
        );
        setCategoryName("");
        setCategories([...categories, response.data.data]);
      } else {
        toast.error(response.data.message || "Failed to add income category");
      }
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          "An error occurred while adding the income category"
      );
    } finally {
      setLoading(false);
    }
  };

  const formatIndianNumber1 = (num: any) => {
    const numStr = Math.floor(num).toString();
    const len = numStr.length;

    if (len <= 3) return numStr;

    let result = "";
    let count = 0;

    for (let i = len - 1; i >= 0; i--) {
      if (count === 3 && i !== len - 1) {
        result = "," + result;
        count = 1;
      } else if (count > 3 && (count - 3) % 2 === 0) {
        result = "," + result;
        count = 1;
      } else {
        count++;
      }
      result = numStr[i] + result;
    }

    return result;
  };

  const handleEditIncome = (income: Income): void => {
    if (!incomePermissions.add_income) {
      toast.error("You do not have permission to edit incomes.");
      return;
    }
    setEditClick(true);
    handleOpen();
    setEditIncome(income);
    setIncomeForm({
      incomeCategory: income.incomeCategory || "",
      date: income.date
        ? new Date(income.date).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      amount: income.amount || 0,
      incomeSourseType: income.incomeSourseType || "",
      description: income.description || "",
      payment_mode: income.payment_mode || "Cash",
      patientId: income.patientId || "",
      // This ensures the existing image URL is preserved in the form
      attachment: income.attachmentUrl || null,
    });
  };

  const handleIncomeSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    console.log("Form submitted with data:", incomeForm);

    if (!incomePermissions.add_income) {
      toast.error("You do not have permission to add or update incomes.");
      return;
    }

    // Reset field errors
    const errors = {
      incomeCategory: false,
      description: false,
      amount: false,
      incomeSourseType: false,
      payment_mode: false,
    };

    let hasErrors = false;
    let missingFields = [];

    // Validate each required field
    if (!incomeForm.incomeCategory) {
      errors.incomeCategory = true;
      missingFields.push("Income Category");
      hasErrors = true;
    }

    if (!incomeForm.description) {
      errors.description = true;
      missingFields.push("Description");
      hasErrors = true;
    }

    if (!incomeForm.amount || incomeForm.amount <= 0) {
      errors.amount = true;
      missingFields.push("Amount");
      hasErrors = true;
    }

    if (!incomeForm.incomeSourseType) {
      errors.incomeSourseType = true;
      missingFields.push("Source Type");
      hasErrors = true;
    }

    if (
      !incomeForm.payment_mode ||
      !["Cash", "Card", "Cheque", "Online", "Bank Transfer"].includes(
        incomeForm.payment_mode
      )
    ) {
      errors.payment_mode = true;
      missingFields.push("Payment Mode");
      hasErrors = true;
    }

    // Update field errors state
    setFieldErrors(errors);

    if (hasErrors) {
      const fieldList = missingFields.join(", ");
      // toast.error(`Please fill in the following required fields: ${fieldList}`);
      return;
    }

    setLoading(true);

    try {
      // For updates without new file, use JSON
      if (editIncome && !(incomeForm.attachment instanceof File)) {
        const incomeData: any = {
          incomeCategory: incomeForm.incomeCategory,
          date: new Date(incomeForm.date).toISOString(),
          amount: incomeForm.amount,
          incomeSourseType: incomeForm.incomeSourseType,
          description: incomeForm.description,
          payment_mode: incomeForm.payment_mode,
          ...(incomeForm.patientId && { patientId: incomeForm.patientId }),
        };
        if (editIncome.attachmentUrl) {
          //@ts-ignore
          incomeData.attachment = editIncome.attachmentUrl;
        }

        // console.log('Update data:', incomeData);

        const response = await axios.patch(
          `${process.env.REACT_APP_ADMIN_API_URL}/income/updateById/${editIncome._id}`,
          incomeData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.isSuccess) {
          toast.success(response.data.message || "Income updated successfully");
          setEditIncome(null);
          setIncomeForm(initialForm);
          setFieldErrors({
            incomeCategory: false,
            description: false,
            amount: false,
            incomeSourseType: false,
            payment_mode: false,
          });
          fetchIncomes();
          handleClose();
        } else {
          toast.error(response.data.message || "Failed to update income");
        }
      } else {
        // Use FormData for new incomes or when updating with new file
        console.log("Creating new income or updating with new file...");
        const formData = new FormData();
        formData.append("incomeCategory", incomeForm.incomeCategory);
        formData.append("date", new Date(incomeForm.date).toISOString());
        formData.append("amount", incomeForm.amount.toString());
        formData.append("incomeSourseType", incomeForm.incomeSourseType);
        formData.append("description", incomeForm.description);
        formData.append("payment_mode", incomeForm.payment_mode);

        if (incomeForm.patientId) {
          formData.append("patientId", incomeForm.patientId);
        }

        // Handle attachment properly
        if (incomeForm.attachment instanceof File) {
          // New file uploaded
          formData.append("file", incomeForm.attachment);
          console.log("File attached:", incomeForm.attachment.name);
        } else if (
          editIncome &&
          typeof incomeForm.attachment === "string" &&
          incomeForm.attachment
        ) {
          // Keep existing image - send the existing URL
          formData.append("existingAttachment", incomeForm.attachment);
        }

        const url = editIncome
          ? `${process.env.REACT_APP_ADMIN_API_URL}/income/updateById/${editIncome._id}`
          : `${process.env.REACT_APP_ADMIN_API_URL}/income/addIncome`;

        const method = editIncome ? "patch" : "post";

        console.log("API call details:", {
          method,
          url,
          editIncome: !!editIncome,
        });

        const response = await axios({
          method,
          url,
          data: formData,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("API response:", response.data);

        if (response.data.isSuccess) {
          toast.success(
            response.data.message ||
              `Income ${editIncome ? "updated" : "added"} successfully`
          );
          setEditIncome(null);
          setIncomeForm(initialForm);
          setFieldErrors({
            incomeCategory: false,
            description: false,
            amount: false,
            incomeSourseType: false,
            payment_mode: false,
          });
          fetchIncomes();
          handleClose();
        } else {
          console.error("API returned error:", response.data);
          toast.error(
            response.data.message ||
              `Failed to ${editIncome ? "update" : "add"} income`
          );
        }
      }
    } catch (err: any) {
      console.error("API call error:", err);
      console.error("Error response:", err.response);
      toast.error(
        err.response?.data?.message ||
          "An error occurred while processing the income"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditIncome = (): void => {
    setEditIncome(null);
    setIncomeForm(initialForm);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleIncomeSubmit(e);
  };

  const handlePrint = () => {
    if (!incomePermissions.print_report) {
      toast.error("You do not have permission to print reports.");
      return;
    }
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(hospitalName, 105, 20, { align: "center" });

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Income Report", 105, 30, { align: "center" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const startDateFormatted = startDate
      ? formatDateOnly(startDate)
      : "N/A";
    const endDateFormatted = endDate
      ? formatDateOnly(endDate)
      : formatDateOnly(new Date());
    doc.text(`Start Date: ${startDateFormatted}`, 15, 55);
    doc.text(`End Date: ${endDateFormatted}`, 15, 61);

    const tableData = incomes.map((income, index) => [
      index + 1,
      income.incomeCategory || "N/A",
      income.description || "N/A",
      formatDateOnly(income.date),
      income.incomeSourseType || "N/A",
      parseFloat(income.amount.toString()).toFixed(1),
      income.payment_mode || "N/A",
      formatDateTime(income.createdAt),
      income.createdBy?.fullName || income.createdBy?.firstName || "N/A",
    ]);

    autoTable(doc, {
      head: [
        [
          "Sr#",
          "Category",
          "Description",
          "Date",
          "Source Type",
          "Amount",
          "Payment Mode",
          "Created At",
          "Created By",
        ],
      ],
      body: tableData,
      startY: 70,
      theme: "plain",
      styles: {
        fontSize: 9,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 22 },
        2: { cellWidth: 30 },
        3: { cellWidth: 20 },
        4: { cellWidth: 23 },
        5: { cellWidth: 18 },
        6: { cellWidth: 24 },
        7: { cellWidth: 28 },
        8: { cellWidth: 20 },
      },
      margin: { left: 10, right: 10 },
      didDrawPage: data => {
        if (data.cursor) {
          const finalY = data.cursor.y + 15;

          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          const totalAmount = incomes.reduce(
            (sum, income) => sum + parseFloat(income.amount.toString()),
            0
          );

          const formatIndianNumber = (num: any) => {
            const numStr = Math.floor(num).toString();
            const len = numStr.length;

            if (len <= 3) return numStr;

            let result = "";
            let count = 0;

            for (let i = len - 1; i >= 0; i--) {
              if (count === 3 && i !== len - 1) {
                result = "," + result;
                count = 1;
              } else if (count > 3 && (count - 3) % 2 === 0) {
                result = "," + result;
                count = 1;
              } else {
                count++;
              }
              result = numStr[i] + result;
            }

            return result;
          };

          doc.text(
            `Total Income: ${formatIndianNumber(totalAmount)}`,
            15,
            finalY + 10
          );

          const convertToWords = (num: any) => {
            const ones = [
              "",
              "One",
              "Two",
              "Three",
              "Four",
              "Five",
              "Six",
              "Seven",
              "Eight",
              "Nine",
            ];
            const teens = [
              "Ten",
              "Eleven",
              "Twelve",
              "Thirteen",
              "Fourteen",
              "Fifteen",
              "Sixteen",
              "Seventeen",
              "Eighteen",
              "Nineteen",
            ];
            const tens = [
              "",
              "",
              "Twenty",
              "Thirty",
              "Forty",
              "Fifty",
              "Sixty",
              "Seventy",
              "Eighty",
              "Ninety",
            ];

            if (num === 0) return "Zero Rupees";

            let result = "";

            const crores = Math.floor(num / 10000000);
            const lakhs = Math.floor((num % 10000000) / 100000);
            const thousands = Math.floor((num % 100000) / 1000);
            const hundreds = Math.floor((num % 1000) / 100);
            const remainder = num % 100;

            const convertTwoDigits = (n: any) => {
              if (n === 0) return "";
              if (n < 10) return ones[n];
              if (n < 20) return teens[n - 10];
              return (
                tens[Math.floor(n / 10)] +
                (n % 10 > 0 ? " " + ones[n % 10] : "")
              );
            };

            if (crores > 0) {
              if (crores < 100) {
                result += convertTwoDigits(crores) + " Crore";
              } else {
                const croreHundreds = Math.floor(crores / 100);
                const croreRemainder = crores % 100;
                if (croreHundreds > 0) {
                  result += ones[croreHundreds] + " Hundred ";
                }
                if (croreRemainder > 0) {
                  result += convertTwoDigits(croreRemainder) + " ";
                }
                result += "Crore";
              }
              if (lakhs > 0 || thousands > 0 || hundreds > 0 || remainder > 0)
                result += ", ";
            }

            if (lakhs > 0) {
              result += convertTwoDigits(lakhs) + " Lakh";
              if (thousands > 0 || hundreds > 0 || remainder > 0)
                result += ", ";
            }

            if (thousands > 0) {
              result += convertTwoDigits(thousands) + " Thousand";
              if (hundreds > 0 || remainder > 0) result += ", ";
            }

            if (hundreds > 0) {
              result += ones[hundreds] + " Hundred";
              if (remainder > 0) result += ", ";
            }

            if (remainder > 0) {
              result += convertTwoDigits(remainder);
            }

            return result.trim() + " Rupees";
          };

          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          const totalInWords = convertToWords(Math.floor(totalAmount));
          doc.text(`Total Income in words: ${totalInWords}`, 15, finalY + 16);
        }
      },
    });

    doc.save(
      `Income_Report-${new Date().toLocaleDateString()}-${new Date().toLocaleTimeString()}.pdf`
    );
  };

  const handleExcelDownload = () => {
    if (!incomePermissions.print_report) {
      toast.error("You do not have permission to download reports.");
      return;
    }

    try {
      const headers = [
        "Sr#",
        "MRN",
        "Patient Name",
        "Category",
        "Description",
        "Date",
        "Source Type",
        "Amount",
        "Payment Mode",
        "Created At",
        "Created By",
      ];

      // Prepare data for Excel
      const excelData = incomes.map((income, index) => ({
        "Sr#": page * rowsPerPage + index + 1,
        MRN: income.patientInfo?.mrn || "",
        "Patient Name": income.patientInfo?.patientName || "",
        Category: income.incomeCategory || "Uncategorized",
        Description: income.description || "",
        Date: formatDateOnly(income.date),
        "Source Type": income.incomeSourseType || "",
        Amount: formatCurrency(parseFloat(income.amount.toString())),
        "Payment Mode": income.payment_mode || "",
        "Created At": formatDateTime(income.createdAt),
        "Created By":
          income.createdBy?.fullName || income.createdBy?.firstName || "N/A",
      }));

      // Create a worksheet
      const ws = XLSX.utils.json_to_sheet(excelData, { header: headers });

      // Set column widths for better formatting
      const columnWidths = [
        { wch: 6 }, // Sr#
        { wch: 12 }, // MRN
        { wch: 20 }, // Patient Name
        { wch: 18 }, // Category
        { wch: 30 }, // Description
        { wch: 12 }, // Date
        { wch: 15 }, // Source Type
        { wch: 15 }, // Amount
        { wch: 15 }, // Payment Mode
        { wch: 20 }, // Created At
        { wch: 15 }, // Created By
      ];
      ws["!cols"] = columnWidths;

      // Create a workbook and append the worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Income Report");

      // Generate Excel file and trigger download
      XLSX.writeFile(
        wb,
        `Income_Report-${new Date().toLocaleDateString()}-${new Date().toLocaleTimeString()}.xlsx`
      );

      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel file. Please try again.");
    }
  };

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditClick(false);
    setIncomeForm(initialForm);
  };

  const handleOpenPatientSearch = () => {
    setOpenPatientSearchDialog(true);
  };

  const handleClosePatientSearch = () => {
    setOpenPatientSearchDialog(false);
    setSearchResults([]);
    setSearchTerm("");
  };
  // Add this useEffect after existing ones

  // Add these functions after existing helper functions
  const handleOpenImageModal = (attachment: File | string | null) => {
    if (attachment instanceof File) {
      setSelectedImageUrl(URL.createObjectURL(attachment));
    } else if (typeof attachment === "string" && attachment) {
      setSelectedImageUrl(`${attachment}`);
    }
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);

    setSelectedImageUrl(null);
  };
  return (
    <Box
      sx={{
        background: "#fff",
        p: 1,
        borderRadius: "12px",
        maxHeight: "90vh",
        overflow: "auto",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#077DD3" },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(7, 125, 211, 0.24)",
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={1}
        sx={{ background: "#fff", borderRadius: "12px" }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: "600", color: "#000" }}>
          Income Management
        </Typography>

        {incomePermissions.add_income && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                navigate("/income&expense/income-delete-history");
              }}
              sx={{
                minWidth: { xs: "auto", md: "100px" },
                fontSize: { xs: "12px", md: "12px" },
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "8px",
                background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                color: "#ffffff",
                boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                  boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": {
                  background: "#b0bec5",
                  color: "#ffffff",
                  opacity: 0.6,
                  cursor: "not-allowed",
                },
                "& .MuiButton-startIcon": {
                  marginRight: { xs: "4px", md: "8px" },
                },
              }}
            >
              view deleted history
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleOpen}
              sx={{
                minWidth: { xs: "auto", md: "100px" },
                fontSize: { xs: "12px", md: "12px" },
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "8px",
                background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                color: "#ffffff",
                boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                  boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": {
                  background: "#b0bec5",
                  color: "#ffffff",
                  opacity: 0.6,
                  cursor: "not-allowed",
                },
                "& .MuiButton-startIcon": {
                  marginRight: { xs: "4px", md: "8px" },
                },
              }}
            >
              Add Income
            </Button>

            {/* <Button
              size="small"
              variant="contained"
              onClick={() => {
                setOpenMainDialog(true);
                fetchCategories();
              }}
              sx={{ backgroundColor: "#077DD3", px: 1, py: 0.5 }}
            >
              Add Category
            </Button> */}
          </Box>
        )}
      </Box>

      <Box sx={{ p: 1 }}>
        {incomePermissions.add_income && (
          <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
              sx: { borderRadius: 2, maxWidth: 500, width: "100%" },
            }}
          >
            <DialogTitle>
              {editClick ? "Update Income" : "Add Income"}
            </DialogTitle>
            <DialogContent>
              <Box component="form" onSubmit={onSubmit} noValidate>
                <Grid container spacing={2} py={1}>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      required
                      fullWidth
                      size="small"
                      sx={{ borderRadius: 2 }}
                      error={fieldErrors.incomeCategory}
                    >
                      <InputLabel
                        id="category-label"
                        sx={{
                          color: fieldErrors.incomeCategory
                            ? "#d32f2f"
                            : undefined,
                          "&.Mui-focused": {
                            color: fieldErrors.incomeCategory
                              ? "#d32f2f"
                              : "#077DD3",
                          },
                        }}
                      >
                        Category {fieldErrors.incomeCategory && "*"}
                      </InputLabel>
                      <Select
                        labelId="category-label"
                        value={incomeForm.incomeCategory}
                        onChange={e => {
                          setIncomeForm({
                            ...incomeForm,
                            incomeCategory: e.target.value,
                          });
                          // Clear error when user starts typing
                          if (fieldErrors.incomeCategory) {
                            setFieldErrors({
                              ...fieldErrors,
                              incomeCategory: false,
                            });
                          }
                        }}
                        label="Category"
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: fieldErrors.incomeCategory
                              ? "#d32f2f"
                              : undefined,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: fieldErrors.incomeCategory
                              ? "#d32f2f"
                              : undefined,
                          },
                        }}
                      >
                        {categories.map(cat => (
                          <MenuItem
                            key={cat._id}
                            value={cat.categoryName}
                            sx={{ fontSize: "11px", py: 0.5 }}
                          >
                            {cat.categoryName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl
                      required
                      fullWidth
                      size="small"
                      sx={{ borderRadius: 2 }}
                      error={fieldErrors.payment_mode}
                    >
                      <InputLabel
                        id="payment-mode-label"
                        sx={{
                          color: fieldErrors.payment_mode
                            ? "#d32f2f"
                            : undefined,
                          "&.Mui-focused": {
                            color: fieldErrors.payment_mode
                              ? "#d32f2f"
                              : "#077DD3",
                          },
                        }}
                      >
                        Payment Mode {fieldErrors.payment_mode && "*"}
                      </InputLabel>
                      <Select
                        labelId="payment-mode-label"
                        value={incomeForm.payment_mode}
                        onChange={e => {
                          setIncomeForm({
                            ...incomeForm,
                            payment_mode: e.target.value,
                          });
                          // Clear error when user selects
                          if (fieldErrors.payment_mode) {
                            setFieldErrors({
                              ...fieldErrors,
                              payment_mode: false,
                            });
                          }
                        }}
                        label="Payment Mode"
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: fieldErrors.payment_mode
                              ? "#d32f2f"
                              : undefined,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: fieldErrors.payment_mode
                              ? "#d32f2f"
                              : undefined,
                          },
                        }}
                      >
                        <MenuItem
                          value="Cash"
                          sx={{ fontSize: "11px", py: 0.5 }}
                        >
                          Cash
                        </MenuItem>
                        <MenuItem
                          value="Card"
                          sx={{ fontSize: "11px", py: 0.5 }}
                        >
                          CARD
                        </MenuItem>
                        <MenuItem
                          value="Online"
                          sx={{ fontSize: "11px", py: 0.5 }}
                        >
                          ONLINE
                        </MenuItem>
                        <MenuItem
                          value="Cheque"
                          sx={{ fontSize: "11px", py: 0.5 }}
                        >
                          CHEQUE
                        </MenuItem>
                        <MenuItem
                          value="Bank Transfer"
                          sx={{ fontSize: "11px", py: 0.5 }}
                        >
                          Bank Transfer
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      type="string"
                      label={`Amount ${fieldErrors.amount ? "*" : ""}`}
                      value={incomeForm.amount}
                      onChange={e => {
                        const input = e.target.value;
                        if (input.length <= 8) {
                          const v = Number(input);
                          setIncomeForm({
                            ...incomeForm,
                            amount: isNaN(v) ? 0 : v,
                          });
                          // Clear error when user starts typing
                          if (fieldErrors.amount) {
                            setFieldErrors({ ...fieldErrors, amount: false });
                          }
                        }
                      }}
                      inputProps={{ maxLength: 8, min: 0, max: 99999999 }}
                      sx={{
                        borderRadius: 2,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: fieldErrors.amount
                              ? "#d32f2f"
                              : undefined,
                          },
                          "&:hover fieldset": {
                            borderColor: fieldErrors.amount
                              ? "#d32f2f"
                              : undefined,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: fieldErrors.amount ? "#d32f2f" : undefined,
                          "&.Mui-focused": {
                            color: fieldErrors.amount ? "#d32f2f" : "#077DD3",
                          },
                        },
                      }}
                      error={fieldErrors.amount}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      type="date"
                      label="Date"
                      value={incomeForm.date}
                      onChange={e =>
                        setIncomeForm({ ...incomeForm, date: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      required
                      fullWidth
                      size="small"
                      sx={{ borderRadius: 2 }}
                      error={fieldErrors.incomeSourseType}
                    >
                      <InputLabel
                        id="source-type-label"
                        sx={{
                          color: fieldErrors.incomeSourseType
                            ? "#d32f2f"
                            : undefined,
                          "&.Mui-focused": {
                            color: fieldErrors.incomeSourseType
                              ? "#d32f2f"
                              : "#077DD3",
                          },
                        }}
                      >
                        Source Type
                      </InputLabel>
                      <Select
                        labelId="source-type-label"
                        value={incomeForm.incomeSourseType}
                        onChange={e => {
                          setIncomeForm({
                            ...incomeForm,
                            incomeSourseType: e.target.value,
                          });
                          // Clear error when user selects
                          if (fieldErrors.incomeSourseType) {
                            setFieldErrors({
                              ...fieldErrors,
                              incomeSourseType: false,
                            });
                          }
                        }}
                        label="Source Type"
                        sx={{
                          fontSize: "12px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: fieldErrors.incomeSourseType
                              ? "#d32f2f"
                              : undefined,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: fieldErrors.incomeSourseType
                              ? "#d32f2f"
                              : undefined,
                          },
                        }}
                      >
                        {sourceTypes?.map((option: SourceType) => (
                          <MenuItem
                            key={option._id}
                            value={option.sourseTypeName}
                            sx={{ fontSize: "11px", py: 0.5 }}
                          >
                            {option.sourseTypeName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{ borderRadius: 2 }}
                    >
                      <Autocomplete
                        id="patient-selection"
                        options={patientOptions}
                        getOptionLabel={option =>
                          `${option.mrn}-${option.patientName}`
                        }
                        value={
                          patientOptions.find(
                            patient => patient._id === incomeForm.patientId
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          setIncomeForm({
                            ...incomeForm,
                            patientId: newValue ? newValue._id : "",
                          });
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Patient Selection"
                            placeholder="Search patients..."
                            size="small" // Yeh overall chhoti height karega
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 35, // yahan apni custom height set karo
                                fontSize: "12px",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "12px", // label size
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "12px", // andar ka input text size
                                padding: "6px 10px", // height aur adjust hogi
                              },
                            }}
                          />
                        )}
                        noOptionsText={
                          loading
                            ? "Loading patients..."
                            : "No recent patients found. Use search to find patients."
                        }
                        loading={loading}
                        sx={{ fontSize: "12px" }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={{ fontSize: "11px", padding: "4px 8px" }}
                          >
                            {option.mrn} - {option.patientName}
                          </li>
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={1}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Tooltip title="Search Patient">
                        <IconButton
                          sx={{
                            background: "#077DD3",
                            color: "white",
                            "&:hover": { background: "#0662c7" },
                            padding: "6px",
                          }}
                          onClick={handleOpenPatientSearch}
                        >
                          <SearchIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      label={`Description ${
                        fieldErrors.description ? "*" : ""
                      }`}
                      multiline
                      minRows={3}
                      value={incomeForm.description}
                      onChange={e => {
                        setIncomeForm({
                          ...incomeForm,
                          description: e.target.value,
                        });
                        // Clear error when user starts typing
                        if (fieldErrors.description) {
                          setFieldErrors({
                            ...fieldErrors,
                            description: false,
                          });
                        }
                      }}
                      sx={{
                        borderRadius: 2,
                        wordBreak: "break-word",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: fieldErrors.description
                              ? "#d32f2f"
                              : undefined,
                          },
                          "&:hover fieldset": {
                            borderColor: fieldErrors.description
                              ? "#d32f2f"
                              : undefined,
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: fieldErrors.description
                            ? "#d32f2f"
                            : undefined,
                          "&.Mui-focused": {
                            color: fieldErrors.description
                              ? "#d32f2f"
                              : "#077DD3",
                          },
                        },
                      }}
                      error={fieldErrors.description}
                    />
                  </Grid>
                  {/* Add this Grid item after the Description field */}
                  <Grid item xs={12}>
                    <Typography variant="body2" fontWeight="bold" mb={1}>
                      attachment
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Button
                        component="label"
                        size="small"
                        sx={{
                          background: "#f1f1f3ff",
                          textTransform: "none",
                          borderRadius: 2,
                          "&:hover": { background: "#e0e0e0" },
                        }}
                      >
                        Upload Image
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (!file.type.startsWith("image/")) {
                              toast.error("Select valid image");
                              return;
                            }
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error("Max size 5MB");
                              return;
                            }
                            setIncomeForm({ ...incomeForm, attachment: file });
                          }}
                        />
                      </Button>

                      {incomeForm.attachment && (
                        <Box>
                          <img
                            src={
                              incomeForm.attachment instanceof File
                                ? URL.createObjectURL(incomeForm.attachment)
                                : `${incomeForm.attachment}`
                            }
                            alt="Attachment"
                            style={{
                              minWidth: 80,
                              maxHeight: "100px",
                              objectFit: "contain",
                              borderRadius: 4,
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              setIncomeForm({ ...incomeForm, attachment: null })
                            }
                          >
                            <DeleteIcon
                              sx={{ fontSize: 16, color: "#FF1F1F" }}
                            />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={onSubmit}
                variant="contained"
                disabled={
                  loading ||
                  !incomeForm.incomeCategory ||
                  !incomeForm.incomeSourseType ||
                  !incomeForm.amount
                }
              >
                {editClick ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <Divider sx={{ my: 1 }} />
        {incomePermissions.view_report && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            {incomePermissions.print_report && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handlePrint}
                  startIcon={<DownloadIcon />}
                  sx={{
                    minWidth: { xs: "auto", md: "100px" },
                    fontSize: { xs: "12px", md: "12px" },
                    fontWeight: "600",
                    textTransform: "none",
                    borderRadius: "8px",
                    background:
                      "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                    color: "#ffffff",
                    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      background:
                        "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      background: "#b0bec5",
                      color: "#ffffff",
                      opacity: 0.6,
                      cursor: "not-allowed",
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: { xs: "4px", md: "8px" },
                    },
                  }}
                >
                  PDF
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleExcelDownload}
                  startIcon={<DownloadIcon />}
                  sx={{
                    minWidth: { xs: "auto", md: "100px" },
                    fontSize: { xs: "12px", md: "12px" },
                    fontWeight: "600",
                    textTransform: "none",
                    borderRadius: "8px",
                    background:
                      "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                    color: "#ffffff",
                    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      background:
                        "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      background: "#b0bec5",
                      color: "#ffffff",
                      opacity: 0.6,
                      cursor: "not-allowed",
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: { xs: "4px", md: "8px" },
                    },
                  }}
                >
                  EXCEL
                </Button>
              </Box>
            )}

            <Grid
              container
              spacing={1}
              sx={{
                alignItems: "flex-end",
                justifyContent: "flex-end",
              }}
            >
              {/* Search By Filter */}
              <Grid item xs={12} md={2}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#000",
                    mb: 0.5,
                  }}
                >
                  Search By
                </Typography>
                <Select
                  className="custom-input"
                  value={searchBy}
                  onChange={e => setSearchBy(e.target.value as string)}
                  fullWidth
                  size="small"
                  sx={{
                    background: "#F5F5FB",
                    borderRadius: "12px",
                    "& fieldset": { border: "none" },
                  }}
                >
                  <MenuItem value="mrn">MRN</MenuItem>
                  <MenuItem value="name">PT Name</MenuItem>
                  <MenuItem value="Category">Category</MenuItem>
                  <MenuItem value="Source Type">Source Type</MenuItem>
                </Select>
              </Grid>

              {/* Search Value Field */}
              <Grid item xs={12} md={2}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#000",
                    mb: 0.5,
                  }}
                >
                  Search Value
                </Typography>
                {searchBy === "mrn" ? (
                  <TextField
                    className="custom-input"
                    type="number"
                    value={searchTerm}
                    onChange={e => {
                      const value = e.target.value;
                      if (Number(value) >= 0) {
                        setSearchTerm(value);
                      }
                    }}
                    onKeyPress={e => {
                      if (e.key === "Enter" && searchTerm.trim()) {
                        fetchIncomes();
                      }
                    }}
                    fullWidth
                    size="small"
                    inputProps={{ min: 0 }}
                    sx={{
                      background: "#F5F5FB",
                      borderRadius: "12px",
                      "& fieldset": { border: "none" },
                    }}
                  />
                ) : searchBy === "name" ? (
                  <TextField
                    className="custom-input"
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === "Enter" && searchTerm.trim()) {
                        fetchIncomes();
                      }
                    }}
                    fullWidth
                    size="small"
                    sx={{
                      background: "#F5F5FB",
                      borderRadius: "12px",
                      "& fieldset": { border: "none" },
                    }}
                  />
                ) : (
                  <Autocomplete
                    freeSolo
                    options={
                      searchBy === "Category"
                        ? categories.map(cat => cat.categoryName)
                        : searchBy === "Source Type"
                        ? sourceTypes.map(option => option.sourseTypeName)
                        : []
                    }
                    value={searchTerm}
                    onChange={(e, newVal) => {
                      setSearchTerm(newVal || "");
                      if (searchBy === "Category" && newVal) {
                        const selectedCategory = categories.find(
                          cat => cat.categoryName === newVal
                        );
                        if (selectedCategory) {
                          handleCategoryChange(selectedCategory);
                        }
                      } else if (!newVal) {
                        fetchIncomes();
                      }
                    }}
                    onInputChange={(e, newInput) => setSearchTerm(newInput)}
                    renderInput={params => (
                      <TextField
                        className="custom-input"
                        {...params}
                        fullWidth
                        size="small"
                        onKeyPress={e => {
                          if (e.key === "Enter" && searchTerm.trim()) {
                            fetchIncomes();
                          }
                        }}
                        sx={{
                          background: "#F5F5FB",
                          borderRadius: "12px",
                          "& fieldset": { border: "none" },
                        }}
                      />
                    )}
                  />
                )}
              </Grid>

              {/* Start Date */}
              <Grid item xs={12} md={2}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#000",
                    mb: 0.5,
                  }}
                >
                  Date Limit
                </Typography>
                <Select
                  className="custom-input"
                  value={dateRange}
                  onChange={e => handleDateRangeChange(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{
                    background: "#F5F5FB",
                    borderRadius: "12px",
                    "& fieldset": { border: "none" },
                  }}
                >
                  <MenuItem value="Today">Today</MenuItem>
                  <MenuItem value="Yesterday">Yesterday</MenuItem>
                  <MenuItem value="Last 7 days">Last 7 days</MenuItem>
                  <MenuItem value="Last 1 month">Last 1 month</MenuItem>
                  <MenuItem value="Last 6 months">Last 6 months</MenuItem>
                  <MenuItem value="Last 1 year">Last 1 year</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </Grid>
              {dateRange === "custom" && (
              <Grid item xs={12} md={2}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#000",
                    mb: 0.5,
                  }}
                >
                  Start Date
                </Typography>
                <TextField
                  className="custom-input"
                  type="date"
                  value={startDate}
                  onChange={e => {
                    setStartDate(e.target.value);
                    setDateRange("custom");
                  }}
                  fullWidth
                  size="small"
                  sx={{
                    background: "#F5F5FB",
                    borderRadius: "12px",
                    "& fieldset": { border: "none" },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              )}

              {/* End Date */}
              {dateRange === "custom" && (
              <Grid item xs={12} md={2}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#000",
                    mb: 0.5,
                  }}
                >
                  End Date
                </Typography>
                <TextField
                  className="custom-input"
                  type="date"
                  value={endDate}
                  onChange={e => {
                    setEndDate(e.target.value);
                    setDateRange("custom");
                  }}
                  fullWidth
                  size="small"
                  sx={{
                    background: "#F5F5FB",
                    borderRadius: "12px",
                    "& fieldset": { border: "none" },
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              )}
            </Grid>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        {incomePermissions.view_report && (
          <Box
            sx={{
              overflowY: "hidden",
            }}
          >
            <DynamicTable
              headers={[
                "SN",
                "MRN",
                "Pt Name",
                "Description",
                "Date",
                "Category",
                "Source Type",
                "Amount",
                "Mode",
                "Created At",
                "Created By",
                "Attachment",
                "Actions",
              ]}
              data={[
                ...incomes.map((income, index) => {
                  return [
                    page * rowsPerPage + index + 1,

                    // MRN - now from patientInfo object
                    income.patientInfo?.mrn || "",
                    income.patientInfo?.patientName || "",
                    income.description || "",
                    formatDateTime(income.date),
                    income.incomeCategory || "Uncategorized",
                    income.incomeSourseType || "",
                    formatCurrency(parseFloat(income.amount.toString())),
                    income.payment_mode || "",
                    // Patient Name - now from patientInfo object

                    formatDateTime(income.createdAt),
                    income?.createdBy?.fullName ||
                      income?.createdBy?.firstName ||
                      "",
                    // Attachment column
                    income.attachmentUrl ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <img
                          src={`${income.attachmentUrl}`}
                          alt="Attachment"
                          style={{
                            width: "40px",
                            height: "35px",
                            objectFit: "cover",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleOpenImageModal(income.attachmentUrl ?? null)
                          }
                          onError={e => {
                            e.currentTarget.style.display = "none";
                            const nextSibling = e.currentTarget
                              .nextSibling as HTMLElement;
                            if (nextSibling)
                              nextSibling.style.display = "block";
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2"></Typography>
                    ),
                  ];
                }),
              ]}
              height={"420px"}
              page={page}
              totalAmount={totalIncomeAmount}
              rowsPerPage={rowsPerPage || 100}
              handleChangePage={(e: any, newPage: any) => setPage(newPage)}
              handleChangeRowsPerPage={(e: any) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              totalRows={totalCount}
              loading={loading}
              actionButtons={
                incomePermissions.add_income
                  ? [
                      {
                        icon: (
                          <EditIcon
                            sx={{
                              height: 20,
                              width: 20,
                              cursor: "pointer",
                              color: () =>
                                !incomePermissions?.can_edit
                                  ? "grey"
                                  : "#FFB800",
                              "&:hover": {
                                color: () =>
                                  !incomePermissions?.can_edit
                                    ? "grey"
                                    : "rgb(133, 96, 1)",
                              },
                            }}
                          />
                        ),
                        onClick: (rowIndex: any) => {
                          if (rowIndex < incomes.length) {
                            handleEditIncome(incomes[rowIndex]);
                          }
                        },
                        disabled: () => !incomePermissions?.can_edit,
                      },
                      {
                        icon: (
                          <DeleteIcon
                            sx={{
                              height: 20,
                              width: 20,
                              cursor: "pointer",
                              color: () =>
                                !incomePermissions?.can_delete
                                  ? "grey"
                                  : "#FF1F1F",
                              "&:hover": {
                                color: () =>
                                  !incomePermissions?.can_delete
                                    ? "grey"
                                    : "rgb(191, 44, 44)",
                              },
                            }}
                          />
                        ),
                        onClick: (rowIndex: any) => {
                          if (rowIndex < incomes.length) {
                            handleOpenDeleteDialog(
                              incomes[rowIndex]._id,
                              "income"
                            );
                          }
                        },
                        disabled: () => !incomePermissions?.can_delete,
                      },
                    ]
                  : []
              }
            />
          </Box>
        )}

        {incomePermissions.add_income && (
          <Dialog
            open={openMainDialog}
            onClose={() => {
              setOpenMainDialog(false);
              setEditCategoryId(null);
              setCategoryName("");
            }}
            fullWidth
            maxWidth="xs"
          >
            <DialogContent>
              <Box sx={{ p: 1 }}>
                <Typography
                  sx={{ fontSize: "16px", fontWeight: "600", color: "#000" }}
                >
                  {editCategoryId
                    ? "Edit Income Category"
                    : "Create Income Category"}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}
                  >
                    Category Name
                  </Typography>
                  <TextField
                    value={categoryName}
                    onChange={e => {
                      setCategoryName(e.target.value);
                      if (editCategoryId) setEditCategoryName(e.target.value);
                    }}
                    fullWidth
                    size="small"
                    sx={{
                      background: "#F5F5FB",
                      borderRadius: "12px",
                      "& fieldset": { border: "none" },
                    }}
                    required
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={loading || !categoryName.trim()}
                    sx={{ backgroundColor: "#055FFC", px: 1, py: 0.5, mt: 1 }}
                  >
                    {loading
                      ? editCategoryId
                        ? "Updating..."
                        : "Creating..."
                      : editCategoryId
                      ? "Update"
                      : "Create Category"}
                  </Button>
                </Box>

                {/* Table listing removed to match expense category layout. */}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setOpenMainDialog(false);
                  setEditCategoryId(null);
                  setCategoryName("");
                }}
                sx={{
                  borderColor: "#055FFC",
                  color: "#055FFC",
                  px: 1,
                  py: 0.5,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}
        {/* Add this Dialog before the closing </Box> */}
        <Dialog
          open={openImageModal}
          onClose={handleCloseImageModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Image Preview</DialogTitle>
          <DialogContent>
            {selectedImageUrl && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img
                  src={selectedImageUrl}
                  alt="Attachment Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "80vh",
                    objectFit: "contain",
                  }}
                  onError={e => {
                    e.currentTarget.style.display = "none";
                    const nextSibling = e.currentTarget
                      .nextSibling as HTMLElement;
                    if (nextSibling) nextSibling.style.display = "block";
                  }}
                />
                <Typography variant="body2" sx={{ display: "none" }}>
                  Unable to load image
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseImageModal}
              variant="outlined"
              size="small"
              sx={{ borderColor: "#055FFC", color: "#055FFC" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        {/* Patient Search Dialog */}
        <Dialog
          open={openPatientSearchDialog}
          onClose={handleClosePatientSearch}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle
            sx={{
              pb: 2,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" }, // column on xs, row on sm+
              alignItems: { xs: "flex-start", sm: "center" }, // flex-start on xs, center on sm+
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Search Patient</Typography>
            <div className="w-2/3">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="search-by-label">Search By</InputLabel>
                    <Select
                      labelId="search-by-label"
                      value={searchBy}
                      onChange={e => setSearchBy(e.target.value)}
                      label="Search By"
                    >
                      <MenuItem value="name">Patient Name</MenuItem>
                      <MenuItem value="mrn">MRN Number</MenuItem>
                      <MenuItem value="cnic">CNIC #</MenuItem>
                      <MenuItem value="phonNumber">Phone #</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Enter ${
                      searchBy === "name"
                        ? "patient name"
                        : searchBy === "mrn"
                        ? "MRN number"
                        : searchBy === "cnic"
                        ? "CNIC number"
                        : "phone number"
                    }`}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === "Enter" && searchTerm.trim()) {
                        handlePatientSearch();
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    variant="contained"
                    onClick={handlePatientSearch}
                    disabled={searchLoading || !searchTerm.trim()}
                    fullWidth
                    sx={{ height: "100%", backgroundColor: "#077DD3" }}
                    startIcon={
                      searchLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                  >
                    {searchLoading ? "Searching..." : "Search"}
                  </Button>
                </Grid>
              </Grid>
            </div>
          </DialogTitle>
          <DialogContent>
            {searchLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>
                  Searching for patients...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                {searchResults.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Patient Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>MRN</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Phone
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Gender
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {searchResults.map(patient => (
                          <TableRow
                            key={patient._id}
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(7, 125, 211, 0.04)",
                              },
                            }}
                          >
                            <TableCell>{patient.patientName}</TableCell>
                            <TableCell>{patient.mrn || ""}</TableCell>
                            <TableCell>{patient.phonNumber || "N/A"}</TableCell>
                            <TableCell>{patient.gender || "N/A"}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleSelectPatient(patient)}
                                sx={{
                                  backgroundColor: "#077DD3",
                                  minWidth: "auto",
                                  px: 2,
                                }}
                              >
                                Select
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : searchTerm && !searchLoading ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      No patients found
                    </Typography>
                    <Typography variant="body2">
                      Try different search criteria or check the spelling
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body1">
                      Enter search criteria and click Search to find patients
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClosePatientSearch} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {incomePermissions.add_income && (
          <DeleteConfirmationModal
            open={isDeleteModalOpen}
            message="Are you sure you want to delete this transaction?."
            handleClose={handleDeleteModalClose}
            handleConfirm={handleDeleteConfirm}
            showReasonField={true}
            reason={deleteReason}
            onReasonChange={setDeleteReason}
          />
        )}
      </Box>
    </Box>
  );
};

export default IncomeCategory;
